import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { TextField } from "../../components/fields";
import { useUnsavedGuard, clearUnsavedWork } from "../../lib/unsaved";
import {
  Button, Field, MoneyField, OptionSelect, Select, Panel } from "../../components/ui";
import { validateVin, validateLegacyVin } from "../../lib/vin";
import { cameraAvailable } from "../../lib/vinScan";
import VinScanner from "./VinScanner";
import { decodeVin, recallTrim, rememberTrim } from "../../lib/vinDecode";
import { parseMoney } from "../../lib/money";
import {
  TRANSMISSIONS, DRIVETRAINS, FUEL_TYPES, BODY_TYPES, DOOR_COUNTS, COLORS,
  SOURCE_TYPES, AUCTION_HOUSES, TITLE_STATUSES,
  normaliseTransmission, normaliseDrivetrain, normaliseFuel, normaliseBody,
} from "../../lib/options";
import {
  checkYear, checkOdometer, checkMoney, checkRequired, warnAskBelowCost,
} from "../../lib/validation";
import { SaveBar } from "../../components/save";
import { mustSave, useSaveState } from "../../lib/saving";

type Errs = Record<string, string | null>;

const BLANK = {
  vin: "", year: "", make: "", model: "", trim: "", bodyType: "",
  engine: "", transmission: "", drivetrain: "", fuelType: "",
  doors: "", exteriorColor: "", odometer: "",
  cost: "", ask: "",
  sourceType: "", auctionHouse: "", sourceOther: "",
  titleStatus: "not_received",
};

export default function AddVehicle({ onDone, onCancel }: {
  onDone: () => void; onCancel: () => void;
}) {
  const [f, setF] = useState({ ...BLANK });

  /* Saved and on its way out. Without this the guard fires on the one
     navigation that is supposed to happen: the form is still full at the
     moment it saves, so leaving looked like abandoning work. */
  const [saved, setSaved] = useState(false);

  /* Anything typed but not yet saved. Compared against the blank form rather
     than tracked with a flag, so clearing a field back to empty counts as
     clean again. */
  useUnsavedGuard(
    !saved && Object.keys(BLANK).some((k) =>
      (f as Record<string, unknown>)[k] !== (BLANK as Record<string, unknown>)[k]),
    "this vehicle");
  const [legacy, setLegacy] = useState(false);
  const [errs, setErrs] = useState<Errs>({});
  const [warn, setWarn] = useState<string | null>(null);
  /* Saving…, then a "Vehicle saved ✓" toast as the list opens, or why not —
     with everything still in the form. */
  const save_ = useSaveState();

  const [scanning, setScanning] = useState(false);
  const canScan = cameraAvailable().ok;
  const [decoding, setDecoding] = useState(false);
  const [decodeNote, setDecodeNote] = useState<string | null>(null);
  const [decodeFail, setDecodeFail] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const touch = (k: string, m: string | null) => setErrs((e) => ({ ...e, [k]: m }));

  useEffect(() => {
    if (legacy) return;
    const vin = f.vin.trim().toUpperCase();
    if (vin.length !== 17 || !validateVin(vin).ok) {
      setDecodeNote(null); setDecodeFail(null); return;
    }

    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setDecoding(true); setDecodeFail(null); setDecodeNote(null);

    decodeVin(vin, ctl.signal).then((res) => {
      if (ctl.signal.aborted) return;
      setDecoding(false);
      if (!res.ok) { if (res.message) setDecodeFail(res.message); return; }

      const v = res.vehicle;
      const remembered = v.year && v.make && v.model
        ? recallTrim(v.year, v.make, v.model) : "";

      /* vPIC speaks its own vocabulary. Map it onto our fixed lists, otherwise
         a decoded value lands on no option at all and looks like a bug. */
      setF((s) => ({
        ...s,
        year: v.year ? String(v.year) : s.year,
        make: v.make || s.make,
        model: v.model || s.model,
        trim: v.trim || remembered || s.trim,
        bodyType: normaliseBody(v.bodyType) || s.bodyType,
        engine: v.engine || s.engine,
        transmission: normaliseTransmission(v.transmission) || s.transmission,
        drivetrain: normaliseDrivetrain(v.drivetrain) || s.drivetrain,
        fuelType: normaliseFuel(v.fuelType) || s.fuelType,
        doors: DOOR_COUNTS.includes(v.doors as never) ? v.doors : s.doors,
      }));
      setErrs((e) => ({ ...e, year: null, make: null, model: null }));

      const bits: string[] = [];
      if (v.year && v.make && v.model) bits.push(`${v.year} ${v.make} ${v.model}`);
      if (res.note) bits.push(res.note);
      if (!v.trim && remembered) bits.push("Trim filled in from last time.");
      else if (v.missing.length)
        bits.push(`No ${v.missing.join(" or ")} in the federal data — pick it below.`);
      setDecodeNote(bits.join("  "));
    });

    return () => ctl.abort();
  }, [f.vin, legacy]);

  /* What the database will assign. Shown rather than typed: the rule is
     enforced by a trigger, so an input here could only ever disagree. */
  const stockPreview = f.vin.trim().toUpperCase().slice(-6);

  /**
   * Is this car already here?
   *
   * A unique index has always refused a duplicate, but only once the whole
   * form was filled in and saved. Asking as soon as the VIN is entered saves
   * typing out a car that is already on the lot.
   *
   * The index only covers vehicles not yet sold, deliberately — a VIN comes
   * back when you re-acquire a car. So a sold one is a note, not an error.
   */
  async function checkVinNotHere(vin: string): Promise<string | null> {
    const v = vin.trim().toUpperCase();
    if (v.length < 5) return null;
    const { data, error } = await supabase
      .from("vehicles")
      .select("stock_no,status,year,make,model")
      .eq("vin", v)
      .limit(5);
    if (error || !data?.length) return null;   // a failed check must not block
    const onLot = data.find((r) => r.status !== "sold");
    if (onLot) {
      return `That VIN is already on the lot as stock ${onLot.stock_no} `
           + `(${onLot.year} ${onLot.make} ${onLot.model}).`;
    }
    return null;
  }

  /** Sold before, so it may legitimately be here again. Worth saying, not
      worth blocking. */
  const [seenBefore, setSeenBefore] = useState<string | null>(null);

  async function noteIfSoldBefore(vin: string) {
    const v = vin.trim().toUpperCase();
    if (v.length < 5) { setSeenBefore(null); return; }
    const { data } = await supabase
      .from("vehicles").select("stock_no,status,year,make,model")
      .eq("vin", v).limit(5);
    const sold = data?.find((r) => r.status === "sold");
    setSeenBefore(sold
      ? `You sold this car before as stock ${sold.stock_no}. Adding it again is fine.`
      : null);
  }

  function validateAll(): boolean {
    const vr = legacy ? validateLegacyVin(f.vin) : validateVin(f.vin);
    const next: Errs = {
      // Required, and not only because the forms need it: the stock number is
      // derived from it, so a vehicle without a VIN cannot be filed at all.
      vin: !f.vin.trim() ? "A VIN is required." : vr.ok ? null : vr.message,
      year: checkYear(f.year),
      odometer: checkOdometer(f.odometer),
      make: checkRequired(f.make, "Make"),
      exteriorColor: checkRequired(f.exteriorColor, "Color"),
      model: checkRequired(f.model, "Model"),
      // Required, not merely well-formed: checkMoney passes on a blank box,
      // which let a car be saved with a $0 cost and a gross that flattered
      // every report it appeared in.
      cost: checkRequired(f.cost, "Purchase price")
            ?? checkMoney(f.cost, "Purchase price"),
      ask: checkMoney(f.ask, "Asking price"),
      sourceType: checkRequired(f.sourceType, "Where it came from"),
      auctionHouse: f.sourceType === "Auction" && !f.auctionHouse
        ? "Which auction?" : null,
      sourceOther:
        (f.sourceType === "Auction" && f.auctionHouse === "Other") && !f.sourceOther.trim()
          ? "Name the auction." : null,
    };
    setErrs(next);
    return Object.values(next).every((v) => v === null);
  }

  /** One readable line for the printed record and the purchase history. */
  function purchasedFrom(): string {
    if (f.sourceType === "Auction") {
      const house = f.auctionHouse === "Other" ? f.sourceOther.trim() : f.auctionHouse;
      return house ? `Auction — ${house}` : "Auction";
    }
    if (f.sourceType && f.sourceOther.trim()) return `${f.sourceType} — ${f.sourceOther.trim()}`;
    return f.sourceType || "Not recorded";
  }

  async function save() {
    if (!validateAll()) { save_.fail("Fix the fields marked in red, then press Save again."); return; }

    const year = Number(f.year);
    // stock_no is never sent. A trigger sets it to the last six of the VIN
    // and ignores anything supplied, so there is one rule and one place it
    // lives. Collisions get a letter suffix rather than an error.
    const row: Record<string, unknown> = {
      vin: f.vin.toUpperCase(),
      vin_non_standard: legacy,
      year,
      make: f.make.trim(),
      model: f.model.trim(),
      trim_level: f.trim.trim() || null,
      body_type: f.bodyType || null,
      engine: f.engine.trim() || null,
      transmission: f.transmission || null,
      drivetrain: f.drivetrain || null,
      fuel_type: f.fuelType || null,
      doors: f.doors ? Number(f.doors) : null,
      exterior_color: f.exteriorColor,
      odometer: Number(f.odometer.replace(/,/g, "")),
      purchase_date: new Date().toISOString().slice(0, 10),
      purchased_from: purchasedFrom(),
      source_type: f.sourceType || null,
      // The purchase price is the only cost recorded here. Everything after
      // it — transport, parts, paint — is an expense line added on the
      // vehicle page, and total_cost_cents is derived and never written.
      purchase_price_cents: parseMoney(f.cost) ?? 0,
      asking_price_cents: parseMoney(f.ask) ?? 0,
      title_status: f.titleStatus,
      status: "available",
    };

    const ok = await save_.run(async () => {
      mustSave(await supabase.from("vehicles").insert(row));
    }, { toast: `${year} ${f.make.trim()} ${f.model.trim()} saved` });
    if (!ok) return;
    if (f.trim.trim()) rememberTrim(year, f.make.trim(), f.model.trim(), f.trim.trim());
    setSaved(true);
    clearUnsavedWork();
    onDone();
  }

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Add a vehicle</h2>
          <p className="pagehead-sub">
            Type or scan the VIN and the rest fills itself in.
          </p>
        </div>
      </div>

      {scanning && (
        <VinScanner
          onClose={() => setScanning(false)}
          onFound={(vin) => { setScanning(false); set("vin")(vin); touch("vin", null); }}
        />
      )}

      <Panel title="The car" rail="record">
      <Field label="VIN" mono maxLength={17} autoCapitalize="characters"
             spellCheck={false} autoComplete="off"
             value={f.vin} onChange={(v) => set("vin")(v.toUpperCase())}
             onBlur={async () => {
               const r = legacy ? validateLegacyVin(f.vin) : validateVin(f.vin);
               if (!f.vin) { touch("vin", null); setSeenBefore(null); return; }
               if (!r.ok) { touch("vin", r.message); setSeenBefore(null); return; }
               touch("vin", await checkVinNotHere(f.vin));
               void noteIfSoldBefore(f.vin);
             }}
             error={errs.vin} />
      {seenBefore && <p className="hint dim">{seenBefore}</p>}

      {canScan && !scanning && (
        <button className="scanbtn" onClick={() => setScanning(true)}>
          <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
            <path d="M3 7V4.5A1.5 1.5 0 0 1 4.5 3H7M13 3h2.5A1.5 1.5 0 0 1 17 4.5V7
                     M17 13v2.5a1.5 1.5 0 0 1-1.5 1.5H13M7 17H4.5A1.5 1.5 0 0 1 3 15.5V13
                     M6 7v6M8.5 7v6M11 7v6M14 7v6"
                  fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Scan the barcode instead
        </button>
      )}

      {decoding && <p className="decode-status">Looking up the VIN</p>}
      {!decoding && decodeNote && <p className="decode-ok">{decodeNote}</p>}
      {!decoding && decodeFail && <p className="decode-warn">{decodeFail}</p>}

      <label className="checkline">
        <input type="checkbox" checked={legacy}
               onChange={(e) => { setLegacy(e.target.checked); touch("vin", null); }} />
        Built before 1981
      </label>

      <div className="grid2">
        <Field label="Year" inputMode="numeric" maxLength={4} value={f.year}
               onChange={set("year")} error={errs.year}
               onBlur={() => touch("year", checkYear(f.year))} />
        <Field label="Odometer" inputMode="numeric" value={f.odometer}
               onChange={set("odometer")} error={errs.odometer}
               onBlur={() => touch("odometer", checkOdometer(f.odometer))} />
      </div>

      <div className="grid2">
        <TextField label="Make" value={f.make} onChange={set("make")} error={errs.make}
               onBlur={() => touch("make", checkRequired(f.make, "Make"))} />
        <TextField label="Model" value={f.model} onChange={set("model")} error={errs.model}
               onBlur={() => touch("model", checkRequired(f.model, "Model"))} />
      </div>

      <div className="grid2">
        <TextField label="Trim" value={f.trim} onChange={set("trim")}
               hint={!f.trim ? "Federal data often omits this." : undefined} />
        <OptionSelect label="Color" value={f.exteriorColor}
                      onChange={(v) => { set("exteriorColor")(v); touch("exteriorColor", checkRequired(v, "Color")); }}
                      options={COLORS} error={errs.exteriorColor} />
      </div>

      </Panel>

      <Panel title="Money" rail="money">
      <div className="grid2">
        <MoneyField label="Purchase price" value={f.cost} onChange={set("cost")}
                    error={errs.cost} hint="What you paid for the car"
                    onBlur={() => touch("cost", checkRequired(f.cost, "Purchase price")
                                        ?? checkMoney(f.cost, "Purchase price"))} />
        <MoneyField label="Asking price" value={f.ask} onChange={set("ask")}
                    error={errs.ask} hint={warn ?? undefined}
                    onBlur={() => {
                      touch("ask", checkMoney(f.ask, "Asking price"));
                      setWarn(warnAskBelowCost(parseMoney(f.ask) ?? 0, parseMoney(f.cost) ?? 0));
                    }} />
      </div>
      <p className="hint dim">
        Transport, parts, paint and everything else go on the vehicle page as
        expenses once you know them. Total cost adds up from there.
      </p>

      </Panel>

      <Panel title="Where it came from" rail="record">
      <OptionSelect label="Source" value={f.sourceType}
                    onChange={(v) => setF((s) => ({
                      ...s, sourceType: v, auctionHouse: "", sourceOther: "",
                    }))}
                    options={SOURCE_TYPES} error={errs.sourceType} />

      {f.sourceType === "Auction" && (
        <Select label="Auction house" value={f.auctionHouse}
                onChange={set("auctionHouse")} placeholder="Choose"
                error={errs.auctionHouse}>
          {AUCTION_HOUSES.map((a) => <option key={a} value={a}>{a}</option>)}
          <option value="Other">Other</option>
        </Select>
      )}

      {f.sourceType === "Auction" && f.auctionHouse === "Other" && (
        <Field label="Which auction" value={f.sourceOther}
               onChange={set("sourceOther")} error={errs.sourceOther} />
      )}

      {(f.sourceType === "Trade-In" || f.sourceType === "Private Seller") && (
        <TextField label="Seller name" value={f.sourceOther} onChange={set("sourceOther")}
               hint="Optional" />
      )}

      </Panel>

      <Panel title="Specification" rail="record"
             note="filled in by the VIN lookup">
      <div className="grid2">
        <OptionSelect label="Body type" value={f.bodyType}
                      onChange={set("bodyType")} options={BODY_TYPES} />
        <OptionSelect label="Doors" value={f.doors}
                      onChange={set("doors")} options={DOOR_COUNTS} />
      </div>
      <div className="grid2">
        <OptionSelect label="Transmission" value={f.transmission}
                      onChange={set("transmission")} options={TRANSMISSIONS} />
        <OptionSelect label="Drivetrain" value={f.drivetrain}
                      onChange={set("drivetrain")} options={DRIVETRAINS} />
      </div>
      <div className="grid2">
        <OptionSelect label="Fuel" value={f.fuelType}
                      onChange={set("fuelType")} options={FUEL_TYPES} />
        <TextField label="Engine" value={f.engine} onChange={set("engine")}
               hint="Decoded, e.g. 4-Cyl 2.0L" />
      </div>

      <div className="item">
        <div className="li">
          <span>Stock number</span>
          <span className="mono">{stockPreview || "enter a VIN"}</span>
        </div>
      </div>
      <p className="hint dim">
        Taken from the last six of the VIN. If another car on the lot already
        ends in those six, this one gets a letter after it.
      </p>

      <Select label="Title" value={f.titleStatus} onChange={set("titleStatus")}>
        {TITLE_STATUSES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </Select>
      </Panel>

      <SaveBar save={save_} onSave={() => void save()} label="Save vehicle"
               alwaysPrimary idle="Not saved yet">
        <Button variant="ghost" onClick={onCancel} disabled={save_.saving}>Cancel</Button>
      </SaveBar>
    </>
  );
}
